'use client';

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from './apiClient';

export interface AppSettings {
  orgName: string;
  timezone: string;
  dateFormat: string;
  currency: string;
  lowStockThreshold: number;
  overdueAlertDays: number;
  smtpHost: string;
  smtpPort: number;
  smtpUser: string;
  smtpPass: string;
  fromName: string;
  fromEmail: string;
  notifWoAssigned: boolean;
  notifWoCompleted: boolean;
  notifWoOverdue: boolean;
  notifLowStock: boolean;
  notifInvReceived: boolean;
  notifInvApproved: boolean;
}

const DEFAULTS: AppSettings = {
  orgName: 'KlugCMMS Operations',
  timezone: 'America/New_York',
  dateFormat: 'MM/DD/YYYY',
  currency: 'USD',
  lowStockThreshold: 10,
  overdueAlertDays: 1,
  smtpHost: '',
  smtpPort: 587,
  smtpUser: '',
  smtpPass: '',
  fromName: '',
  fromEmail: '',
  notifWoAssigned: true,
  notifWoCompleted: true,
  notifWoOverdue: true,
  notifLowStock: true,
  notifInvReceived: false,
  notifInvApproved: false,
};

interface SettingsContextValue {
  settings: AppSettings;
  loading: boolean;
  refetch: () => Promise<void>;
  formatCurrency: (amount: number) => string;
  formatDate: (date: string | Date | null | undefined) => string;
}

const SettingsContext = createContext<SettingsContextValue>({
  settings: DEFAULTS,
  loading: true,
  refetch: async () => {},
  formatCurrency: (n) => `$${n.toFixed(2)}`,
  formatDate: (d) => (d ? new Date(d).toLocaleDateString() : '—'),
});

function buildFormatCurrency(currency: string) {
  return (amount: number) =>
    new Intl.NumberFormat('en-US', { style: 'currency', currency, minimumFractionDigits: 2 }).format(amount);
}

function buildFormatDate(dateFormat: string) {
  return (date: string | Date | null | undefined): string => {
    if (!date) return '—';
    const d = new Date(date);
    if (isNaN(d.getTime())) return '—';
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = String(d.getFullYear());
    switch (dateFormat) {
      case 'Y-m-d': case 'YYYY-MM-DD': return `${yyyy}-${mm}-${dd}`;
      case 'm-d-Y': case 'MM-DD-YYYY': return `${mm}-${dd}-${yyyy}`;
      case 'd-m-Y': case 'DD-MM-YYYY': return `${dd}-${mm}-${yyyy}`;
      case 'Y/m/d': case 'YYYY/MM/DD': return `${yyyy}/${mm}/${dd}`;
      case 'm/d/Y': case 'MM/DD/YYYY': return `${mm}/${dd}/${yyyy}`;
      case 'd/m/Y': case 'DD/MM/YYYY': return `${dd}/${mm}/${yyyy}`;
      default:                         return `${yyyy}-${mm}-${dd}`;
    }
  };
}

export function SettingsProvider({ children }: { children: React.ReactNode }) {
  const [settings, setSettings] = useState<AppSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);

  const refetch = useCallback(async () => {
    try {
      const res = await apiRequest('/api/settings');
      const json = await res.json();
      if (json.success) setSettings({ ...DEFAULTS, ...json.data });
    } catch {
      // keep defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const token = typeof window !== 'undefined' ? localStorage.getItem('klugcmms-access-token') : null;
    if (token) refetch();
    else setLoading(false);
  }, [refetch]);

  const formatCurrency = buildFormatCurrency(settings.currency);
  const formatDate = buildFormatDate(settings.dateFormat);

  return (
    <SettingsContext.Provider value={{ settings, loading, refetch, formatCurrency, formatDate }}>
      {children}
    </SettingsContext.Provider>
  );
}

export function useSettings() {
  return useContext(SettingsContext);
}
